import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * Card surface variants — single source of truth for the four historical
 * card implementations (shadcn `Card`, `.tls-card`, `.tlsr-card`, and
 * `collection/Card.tsx`). The `raised` and `refresh` variants intentionally
 * emit the existing CSS class names so per-card behavior (per-instance
 * `--tls-card-accent-r` overrides on `raised`, `details[open]` state on
 * `refresh`) keeps working without divergence.
 *
 * Reach for `<Card surface="…" />` for div surfaces; use `cardVariants({…})`
 * directly when the surface element must be a `<details>`, `<article>`,
 * `<button>`, or framer-motion component.
 */
const cardVariants = cva("", {
  variants: {
    surface: {
      default:
        "flex flex-col gap-6 border-t border-[color:var(--tls-border-subtle)] py-6 text-[var(--tls-fg-strong)] shadow-none transition-[background-color,border-color,color,box-shadow] duration-[var(--motion-duration-standard)] ease-[var(--motion-ease-standard)]",
      raised: "tls-card",
      refresh: "tlsr-card",
    },
    active: {
      true: "is-active",
      false: "",
    },
  },
  compoundVariants: [
    { surface: "default", active: true, class: "border-[color:var(--tls-border-strong)]" },
  ],
  defaultVariants: {
    surface: "default",
    active: false,
  },
})

type CardProps = React.ComponentProps<"div"> &
  VariantProps<typeof cardVariants>

function Card({ className, surface, active, ...props }: CardProps) {
  return (
    <div
      data-slot="card"
      data-surface={surface ?? "default"}
      className={cn(cardVariants({ surface, active }), className)}
      {...props}
    />
  )
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6",
        className
      )}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn("leading-none font-semibold tracking-[-0.01em]", className)}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-sm text-[var(--tls-fg-muted)]", className)}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      )}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("px-6", className)}
      {...props}
    />
  )
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center px-6 [.border-t]:pt-6", className)}
      {...props}
    />
  )
}

/* eslint-disable react-doctor/only-export-components */
export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
  cardVariants,
}
export type { CardProps }
/* eslint-enable react-doctor/only-export-components */
